import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const JobDetailsModal = ({ open, onClose, job, queueName }) => {
  if (!job) { return null; }

  const formatTimestamp = (timestamp) => {
    if (!timestamp) { return "N/A"; }
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (start, end) => {
    if (!start || !end) { return "N/A"; }
    const duration = end - start;
    return `${Math.round(duration / 1000)}s`;
  };

  const getJobStatus = () => {
    if (job.failedReason) { return { label: "Failed", color: "error" }; }
    if (job.finishedOn) { return { label: "Completed", color: "success" }; }
    if (job.processedOn) { return { label: "Processing", color: "info" }; }
    return { label: "Waiting", color: "default" };
  };

  const status = getJobStatus();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">
              Job Details - {job.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Queue: {queueName} | ID: {job.id}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label={status.label} color={status.color} size="small" />
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Timing Information
          </Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">Created</Typography>
              <Typography variant="body1">{formatTimestamp(job.timestamp)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Started</Typography>
              <Typography variant="body1">{formatTimestamp(job.processedOn)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Finished</Typography>
              <Typography variant="body1">{formatTimestamp(job.finishedOn)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Duration</Typography>
              <Typography variant="body1">
                {formatDuration(job.processedOn, job.finishedOn)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Attempts</Typography>
              <Typography variant="body1">{job.attemptsMade || 0}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Progress</Typography>
              <Typography variant="body1">{job.progress || "N/A"}</Typography>
            </Box>
          </Box>
        </Box>

        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Job Data</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box component="pre" sx={{
              backgroundColor: "grey.100",
              p: 2,
              borderRadius: 1,
              overflow: "auto",
              fontSize: "0.875rem",
              fontFamily: "monospace"
            }}>
              {JSON.stringify(job.data, null, 2)}
            </Box>
          </AccordionDetails>
        </Accordion>

        {job.opts && Object.keys(job.opts).length > 0 && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Job Options</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box component="pre" sx={{
                backgroundColor: "grey.100",
                p: 2,
                borderRadius: 1,
                overflow: "auto",
                fontSize: "0.875rem",
                fontFamily: "monospace"
              }}>
                {JSON.stringify(job.opts, null, 2)}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {job.returnvalue && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Return Value</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box component="pre" sx={{
                backgroundColor: "grey.100",
                p: 2,
                borderRadius: 1,
                overflow: "auto",
                fontSize: "0.875rem",
                fontFamily: "monospace"
              }}>
                {JSON.stringify(job.returnvalue, null, 2)}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {job.failedReason && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" color="error">Error Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="error" gutterBottom>
                {job.failedReason}
              </Typography>
              {job.stacktrace && (
                <Box component="pre" sx={{
                  backgroundColor: "error.light",
                  color: "error.contrastText",
                  p: 2,
                  borderRadius: 1,
                  overflow: "auto",
                  fontSize: "0.75rem",
                  fontFamily: "monospace",
                  mt: 1
                }}>
                  {job.stacktrace.join("\n")}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default JobDetailsModal;
